// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Edit from '../forms/locations/Edit';
import { location } from '../../requests';

type Props = unknown;

interface State {
  update?: any;
  loading: boolean;
  data?: any[];
  showGrid?: boolean;
}

export default class Locations extends React.Component <Props, State> {
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.buttons = {
      edit: true,
      add: true,
      del: true,
      toggleItem: true,
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      showGrid: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const locationTable: any = await location('list');
      if (this.mounted) {
        this.setState({
          data: locationTable,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId: string): void => {
    const tabId: string = 'LE';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Locations Edit',
      component: Edit,
      props: {
        rowId,
        report: 'LOCATIONS',
        title: 'Locations',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (value: any): void => {
    window.store.dispatch(removeTab('LE'));
    const rowId: string = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: any): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      location('del', value.id)
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: any = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      address: 'Address',
      latitude: 'Latitude',
      longitude: 'Longitude'
    };
    const columnsToHide: any = [];
    const columnStyles: any = {
      code: { textAlign: 'right' },
      name: { textAlign: 'left' },
      address: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };

    return (
      <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
        />
      </div>
    );
  }
}
